import { getErrorMessage } from '@/utils/errorUtils';

import { supabase } from '../client';
import { TeeBox, getManualPlayerId } from '@/types';
import { getOrCreateTeeBox } from './teeBoxService';
import { findEligibleGameIds } from './gameLinkingUtils';
import type { TablesUpdate } from '../types';

/**
 * Update an existing score record
 */
export const updateScore = async (
  scoreId: string,
  scoreData: {
    tee?: TeeBox;
    score_type?: 'birdie' | 'eagle' | 'double_eagle';
    score_date?: string;
    notes?: string;
    gameIds?: string[];
    presentPlayerIds?: string[];
  }
) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.error('No authenticated user found');
      return { error: 'Authentication required' };
    }

    const updateData: TablesUpdate<'scores'> = {};

    if (scoreData.score_type) updateData.score_type = scoreData.score_type;
    if (scoreData.score_date) updateData.score_date = scoreData.score_date;
    if (scoreData.notes !== undefined) updateData.notes = scoreData.notes;

    // If tee is being updated, find or create the tee box
    if (scoreData.tee) {
      const { data: existingScore, error: scoreError } = await supabase
        .from('scores')
        .select('hole_number, course_id')
        .eq('id', scoreId)
        .single();

      if (scoreError || !existingScore) {
        console.error('Error finding score:', scoreError);
        return { error: `Score not found: ${scoreError?.message}` };
      }

      const { data: hole, error: holeError } = await supabase
        .from('holes')
        .select('id')
        .eq('course_id', existingScore.course_id)
        .eq('hole_number', existingScore.hole_number)
        .single();

      if (holeError || !hole) {
        console.error('Error finding hole:', holeError);
        return { error: `Hole not found: ${holeError?.message}` };
      }

      const { data: teeBoxId, error: teeBoxError } = await getOrCreateTeeBox(hole.id, scoreData.tee);

      if (teeBoxError || !teeBoxId) {
        console.error('Error with tee box:', teeBoxError);
        return { error: `Tee box error: ${teeBoxError}` };
      }

      updateData.tee = scoreData.tee;
      updateData.tee_box_id = teeBoxId;
    }

    // Update score record
    const { data: updatedScore, error: updateError } = await supabase
      .from('scores')
      .update(updateData)
      .eq('id', scoreId)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating score:', updateError);
      return { error: updateError.message };
    }

    // Update player presence if provided
    if (scoreData.presentPlayerIds) {
      const presentPlayerIds = [...scoreData.presentPlayerIds];
      if (!presentPlayerIds.includes(user.id)) {
        presentPlayerIds.push(user.id);
      }

      // Delete existing presence records
      const { error: deletePresenceError } = await supabase
        .from('score_presence')
        .delete()
        .eq('score_id', scoreId);

      if (deletePresenceError) {
        console.error('Error deleting player presence:', deletePresenceError);
        return { error: deletePresenceError.message };
      }

      // Insert new presence records (split real vs manual player IDs)
      if (presentPlayerIds.length > 0) {
        const presenceData = presentPlayerIds.map(playerId => {
          const mpId = getManualPlayerId(playerId);
          if (mpId) {
            return { score_id: scoreId, profile_id: null, manual_player_id: mpId };
          }
          return { score_id: scoreId, profile_id: playerId, manual_player_id: null };
        });

        const { error: insertPresenceError } = await supabase
          .from('score_presence')
          .insert(presenceData);

        if (insertPresenceError) {
          console.error('Error recording player presence:', insertPresenceError);
          return { error: insertPresenceError.message };
        }

        // Update game links using batch queries
        try {
          // Delete existing links
          const { error: deleteError } = await supabase
            .from('score_games')
            .delete()
            .eq('score_id', scoreId);

          if (deleteError) {
            console.error('Error deleting score game links:', deleteError);
          }

          // Get the score's info for re-linking
          const { data: scoreInfo } = await supabase
            .from('scores')
            .select('course_id, user_id, score_date')
            .eq('id', scoreId)
            .single();

          if (scoreInfo) {
            const eligibleGameIds = await findEligibleGameIds({
              userId: scoreInfo.user_id,
              courseId: scoreInfo.course_id,
              scoreDate: scoreInfo.score_date,
              presentPlayerIds,
            });

            if (eligibleGameIds.length > 0) {
              const scoreGameLinks = eligibleGameIds.map(gameId => ({
                score_id: scoreId,
                game_id: gameId
              }));

              const { error: insertError } = await supabase
                .from('score_games')
                .insert(scoreGameLinks);

              if (insertError) {
                console.error('Error linking score to games:', insertError);
              }
            }
          }
        } catch (error) {
          console.error('Error auto-linking score to games:', error);
        }
      }
    }

    return { data: updatedScore };
  } catch (error) {
    console.error('Unexpected error in updateScore:', error);
    return { error: getErrorMessage(error) };
  }
};
